// @ts-strict-ignore
import { createFilterOptions } from '@material-ui/lab/Autocomplete';

import { Text } from '@/atoms/Text/Text';
import { NeedlAutocompletePopper } from '@/components/NeedlAutocomplete';
import type { CreatableOptionType } from '@/components/NeedlAutocomplete/NeedlAutocomplete';
import { NeedlAutocomplete } from '@/components/NeedlAutocomplete/NeedlAutocomplete';
import { useTagSelectorContext } from '@/components/UserTags/TagAutocomplete/TagsSelector/context';
import SearchIcon from '@/icons/search.svg?react';

type Option = CreatableOptionType | string;

const filter = createFilterOptions<Option>();

export const Selector = ({ forceMount, portal }: SelectorType) => {
  const {
    disabled,
    id,
    open,
    anchorEl,
    disableAutocomplete,
    closeSelector,
    userTagsSet,
    onChange,
    options,
    primaryButtonHandler,
    selectedTags,
  } = useTagSelectorContext();

  if (disabled) {
    return null;
  }

  return (
    <NeedlAutocompletePopper<Option, true>
      id={id}
      open={open}
      portal={portal}
      forceMount={forceMount}
      anchorEl={anchorEl}
      placement='bottom-start'
      multiple
      options={options}
      clearOnBlur={false}
      disabled={disableAutocomplete}
      onClose={closeSelector}
      value={selectedTags ?? []}
      onChange={onChange}
      getOptionLabel={(option: Option) => {
        if (typeof option === 'string') {
          return option;
        }
        if (option.inputValue) {
          return option.inputValue;
        }
        throw new Error('Unexpected option type');
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const input = params.inputValue.trim();

        if (input !== '' && !userTagsSet.has(input)) {
          filtered.push({
            inputValue: params.inputValue,
            title: (
              <span id={`user-input-${params.inputValue}`}>
                Add <b>&quot;{params.inputValue}&quot;</b>
              </span>
            ),
          });
        }

        return filtered;
      }}
      renderOption={(option, { selected }) => {
        return (
          <>
            {typeof option === 'string' ? (
              <NeedlAutocomplete.Checkbox checked={selected} />
            ) : (
              <NeedlAutocomplete.AddIcon />
            )}
            <Text>
              {typeof option === 'string'
                ? option
                : option.title || option.inputValue}
            </Text>
          </>
        );
      }}
      placeholder='Find tag or create one'
      endAdornment={<SearchIcon />}
      noOptionsText='Please create a Tag'
      primaryButtonText='Done'
      primaryButtonHandler={primaryButtonHandler}
    />
  );
};

type SelectorType = {
  forceMount?: boolean;
  portal?: boolean;
};
